import User from "@/Layouts/User";
import Head from "next/head";
import { useBuy } from "@/context/buy";
import { useEffect, useState, useRef } from "react";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import * as Yup from "yup";
import PhoneInput from "react-phone-input-2";
import { useRouter } from "next/router";
import { auth, RecaptchaVerifier } from "@/firebase";
import { encryptInteger } from "@/Utils/encryptInteger";
import { useLoading } from "@/context/loading";
import MetaTags from "@/Components/SEO/MetaTags";
import { Schema } from "@/Components/SEO/Schema";
import withBaseUrl from "@/Utils/const";
import { motion } from "framer-motion";
import Cookies from "js-cookie";
import { sendSignInLinkToEmail } from "firebase/auth";

export default function index() {
  const router = useRouter();
  const { isLoading } = useLoading();
  const { directOpen, setLoggedUserDetails, setOpenModal } = useBuy();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitingLink, setSubmittingLink] = useState(false);
  const [currentTab, setCurrentTab] = useState("magicLink");
  const [isUpdateProfile, setIsUpdateProfile] = useState(false);
  const [updateProfileData, setUpdateProfileData] = useState({
    email: "",
    web_token: ""
  });
  const [magicLinkData, setMagicLinkData] = useState({
    email: "",
    name: "",
    phone_number: "",
    country_code: "+1",
    agree: false,
    is_sms_on: false,
  });
  const [magicLinkErrors, setMagicLinkErrors] = useState({
    email: "",
    name: "",
    phone_number: "",
    country_code: "",
    agree: "",
  });

  useEffect(() => {
    let loggedInUser = false;
    let encryptedData = localStorage.getItem("user_data");
    if (encryptedData) {
      loggedInUser = true;
      router.back();
    }
    const tempRegisterData = localStorage.getItem("temp_register_data");
    if (tempRegisterData) {
      const data = JSON.parse(tempRegisterData);
      setUpdateProfileData(data);
      setMagicLinkData((prev) => ({
        ...prev,
        email: data.email,
      }));
      setIsUpdateProfile(true);
    }
  }, []);

  const formik = useFormik({
    initialValues: {
      email: isUpdateProfile ? updateProfileData.email : "",
      password: "",
      name: "",
      phone_number: "",
      country_code: "+1",
      agree: false,
      is_sms_on: false,
    },

    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email format")
        .required("Email is required"),
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Password is required"),
      name: Yup.string().required("Name is required"),
      phone_number: Yup.string()
        .matches(/^[0-9]+$/, "Phone number must contain only digits")
        .min(10, "Phone number must be at least 10 digits")
        .required("Phone Number is required"),
      agree: Yup.boolean().oneOf([true], "You must agree to the terms"),
    }),

    onSubmit: async (values) => {
      setIsSubmitting(true);

      try {
        const fullPhone = `+${values.phone_number}`;

        if (!window.recaptchaVerifier) {
          window.recaptchaVerifier = new RecaptchaVerifier(
            auth,
            "submit-button-id",
            {
              size: "invisible",
              callback: () => {
                console.log("reCAPTCHA verified");
              },
              "expired-callback": () => {
                window.recaptchaVerifier = null;
                toast.error("reCAPTCHA expired. Please try again.");
              },
            }
          );
        }

        try {
          await window.recaptchaVerifier.render();
        } catch (error) {
          console.error("reCAPTCHA render error:", error);
          window.recaptchaVerifier = null;
          toast.error("Error initializing reCAPTCHA. Please try again.");
          return;
        }

        let location_data = {};
        if (!directOpen) {
          location_data = JSON.parse(
            sessionStorage.getItem("location_data")
          );
        } else {
          location_data = {
            city: values.city,
            state: values.state,
          }
        }

        const formData = new FormData();
        const rawPhoneNumber = values.phone_number;

        formData.append("action", "createAccount");
        formData.append("email", values.email);
        formData.append("password", values.password);
        formData.append("name", values.name);
        formData.append("phone_number", rawPhoneNumber);
        formData.append("country_code", values.country_code);
        formData.append("agree", values.agree);
        formData.append('city', 'Denver');
        formData.append('state', 'CO');
        formData.append("is_sms_on", values.is_sms_on ? 1 : 0);

        const response = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageUser`,
          {
            method: "POST",
            body: formData,
          }
        );

        const data = await response.json();

        if (data.status) {
          toast.success("Account created successfully!");
          const storingData = {
            profile: data?.profile,
            web_token_expires_at: data?.web_token_expires_at,
            web_token: data?.web_token,
            name: values?.name,
            state: values?.state,
            user_type: data?.data?.user_type,
          };
          const encryptedData = encryptInteger(JSON.stringify(storingData));
          localStorage.setItem("user_data", encryptedData);
          setLoggedUserDetails({
            status: true,
            data: storingData,
            remove: false,
          });
          localStorage.removeItem("temp_register_data");

          let back_url = sessionStorage.getItem("last_url");
          router.push(back_url ?? "/");
        } else {
          toast.error(data.message || "Failed to create account");
        }
      } catch (error) {
        console.error("Form submission error:", error);
        toast.error("Something went wrong. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    },
  });
  const getBreadcrumbs = () => {
    return [
      {
        name: "Home",
        url: withBaseUrl("/"),
      },
      {
        name: "Account",
        url: withBaseUrl("/register"),
      },
    ];
  };

  const validateMagicLinkData = (data) => {
    const errors = {};
    if (!data.name.trim()) errors.name = "Name is required";
    if (!data.email.trim()) errors.email = "Email is required";
    if (!data.phone_number.trim()) {
      errors.phone_number = "Phone number is required";
    } else if (!/^[0-9]+$/.test(data.phone_number)) {
      errors.phone_number = "Phone number must contain only digits";
    } else if (data.phone_number.length < 10) {
      errors.phone_number = "Phone number must be at least 10 digits";
    }
    if (!data.agree) errors.agree = "You must agree to the terms";
    return errors;
  };

  const sendMagicLink = async () => {
    setSubmittingLink(true);

    const errors = validateMagicLinkData(magicLinkData);
    if (Object.keys(errors).length > 0) {
      setMagicLinkErrors(errors);
      console.log("Validation failed:", errors);
      setSubmittingLink(false);
      return;
    } else {
      setMagicLinkErrors({});
    }

    const formData = new FormData();
    const rawPhoneNumber = magicLinkData.phone_number;
    formData.append("email", magicLinkData.email);
    formData.append("name", magicLinkData.name);
    formData.append("phone_number", rawPhoneNumber);
    formData.append("country_code", magicLinkData.country_code);
    formData.append("city", "Denver");
    formData.append("state", "CO");
    formData.append("agree", magicLinkData.agree ? 1 : 0);
    formData.append("is_sms_on", magicLinkData.is_sms_on ? 1 : 0);

    console.log("Sending formData:", {
      email: magicLinkData.email,
      name: magicLinkData.name,
      phone_number: magicLinkData.phone_number,
      country_code: magicLinkData.country_code,
      agree: magicLinkData.agree,
      is_sms_on: magicLinkData.is_sms_on,
      isUpdateProfile,
    });

    if (isUpdateProfile) {
      formData.append("action", "addProfile");
      formData.append("token", updateProfileData.web_token);

      const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageUser`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${updateProfileData.web_token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (data.status) {
        toast.success(data.message);
        const values = data.data;
        const storingData = {
          profile: values.profile,
          web_token_expires_at: values.web_token_expires_at,
          web_token: values.web_token,
          name: values?.name,
          state: values?.state,
          user_type: values?.user_type,
        };
        const encryptedData = encryptInteger(JSON.stringify(storingData));
        Cookies.set("user_data", JSON.stringify(storingData), {
          expires: 10,
          path: "/",
          secure: true,
          sameSite: "Lax",
        });

        setLoggedUserDetails({
          status: true,
          data: storingData,
          remove: false,
        });
        localStorage.setItem("user_data", encryptedData);
        localStorage.removeItem("temp_register_data");
        setIsUpdateProfile(false);
        router.push('/');
      } else {
        toast.error(data.message || "Failed to update profile");
      }

    } else {
      formData.append("action", "createAccount");

      const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageUser`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (data.status) {
        const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageUser`, {
          method: "POST",
          body: new URLSearchParams({
            action: "sentMagicLink",
            email: magicLinkData?.email,
          }),
        });
        const data = await response.json();
        if (data.status) {
          toast.success("Magic link sent successfully");
        } else {
          toast.error(data?.message || "Error in Login Please Try Again");
          formik.resetForm();
        }
        formik.resetForm();
        setMagicLinkData({
          name: "",
          email: "",
          phone_number: "",
          country_code: "+1",
          agree: false,
          is_sms_on: false,
        });
      } else {
        toast.error(data.message || "Failed to create account");
      }
    }

    setSubmittingLink(false);
  };

  return (
    <User>
      <MetaTags
        title="Register | Qoncert"
        description="Create your Qoncert account to book tickets, manage shows, and connect with artists."
        ogImage="/assets/logo.webp"
        canonicalUrl="/register"
        noindex={true}
      />
      <Head>
        <title>Register | Qoncert</title>
        <Schema breadcrumbs={getBreadcrumbs()} />
      </Head>
      <div
        id="CreateUserDetailsModal"
        className="font-urbanist py-10 lg:py-20 "
      >
        <div className="layout_bg_image hidden lg:block">
          <span className="bg_inner_shadow"></span>
        </div>
        <div
          className="flex flex-col md:flex-row justify-start overflow-auto lg:items-center lg:justify-center   h-full inset-0 px-7 md:px-0"
          onClick={() =>
            setOpenModal((prev) => ({
              ...prev,
              create: false,
            }))
          }
        >
          <motion.div
            id="login-modal"
            initial={{ opacity: 0, y: 20 }}
            animate={!isLoading ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-[#17191d] relative h-fit w-full max-w-[620px] md:my-[20px] overflow-y-auto overflow-x-hidden px-[20px] pb-[40px] rounded-[16px] lg:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="hidden lg:block"
            >
              {!directOpen && (
                <div className="breadcrumbs w-fit mx-auto pt-10 max-w-[550px]">
                  <div
                    className={`flex gap-4 items-center bread-item ${active == 0 ? "active-bread" : ""
                      }`}
                  >
                    <p>Account</p>
                  </div>
                  <div className="bread-icons gap-3 flex items-center">
                    <svg
                      className={` ${active == 0 ? "active-bread" : ""}`}
                      width="25"
                      height="1"
                      viewBox="0 0 25 1"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <line
                        y1="0.5"
                        x2="25"
                        y2="0.5"
                        stroke={`${active == 0 ? "#FA2CB4" : "#9E9E9E"} `}
                      />
                    </svg>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_1708_9606)">
                        <path
                          d="M12 22C6.47699 22 2 17.523 2 12C2 6.477 6.47699 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM11.003 16L18.073 8.929L16.659 7.515L11.003 13.172L8.17399 10.343L6.75999 11.757L11.003 16Z"
                          fill="#9E9E9E"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_1708_9606">
                          <rect width="24" height="24" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    <svg
                      width="25"
                      height="1"
                      viewBox="0 0 25 1"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <line
                        y1="0.5"
                        x2="25"
                        y2="0.5"
                        stroke={`${active == 1 ? "#FA2CB4" : "#9E9E9E"} `}
                      />
                    </svg>
                  </div>
                  <div
                    className={`flex gap-4 items-center bread-item ${active == 1 ? "active-bread" : ""
                      }`}
                  >
                    <p>Payment</p>
                  </div>
                  <div className="bread-icons gap-3 flex items-center">
                    <svg
                      width="25"
                      height="1"
                      viewBox="0 0 25 1"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <line
                        y1="0.5"
                        x2="25"
                        y2="0.5"
                        stroke={`${active == 1 ? "#FA2CB4" : "#9E9E9E"} `}
                      />
                    </svg>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_1708_9606)">
                        <path
                          d="M12 22C6.47699 22 2 17.523 2 12C2 6.477 6.47699 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM11.003 16L18.073 8.929L16.659 7.515L11.003 13.172L8.17399 10.343L6.75999 11.757L11.003 16Z"
                          fill="#9E9E9E"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_1708_9606">
                          <rect width="24" height="24" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    <svg
                      width="25"
                      height="1"
                      viewBox="0 0 25 1"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <line
                        y1="0.5"
                        x2="25"
                        y2="0.5"
                        stroke={`${active == 2 ? "#FA2CB4" : "#9E9E9E"} `}
                      />
                    </svg>
                  </div>
                  <div
                    className={`flex gap-4 items-center bread-item ${active == 2 ? "active-bread" : ""
                      }`}
                  >
                    <p>Confirmation</p>
                  </div>
                </div>
              )}
            </motion.div>
            {currentTab == "magicLink" ? (
              <motion.div initial={{ opacity: 0 }} animate={!isLoading ? { opacity: 1 } : { opacity: 0 }} transition={{ duration: 0.5, delay: 0.4 }}>
                <div className="mt-6 lg:mt-0 md:px-8">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    className="lg:mt-11 createeAcc text-center text-xl"
                  >
                    <p> {isUpdateProfile ? "Add Profile" : "Create Account"}</p>
                  </motion.div>
                  <motion.form
                    initial={{ opacity: 0 }}
                    animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    action=""
                    className="mt-5 lg:pl-0 lg:mt-7"
                  >
                    <div className="">
                      <div className="form-item flex flex-col gap-2 ">
                        <label
                          className="text-[10px] lg:text-base text-[#718096]"
                          htmlFor="last_name"
                        >
                          Name
                        </label>
                        <div className="relative">
                          <input
                            className="bg-[#36383f] w-full text-[#fff] focus:outline-none text-[12px] lg:text-base rounded-[5px] p-3 lg:p-3 "
                            placeholder="Enter your Name"
                            type="text"
                            name="name"
                            onChange={(e) => {
                              setMagicLinkData({
                                ...magicLinkData,
                                name: e.target.value,
                              });
                            }}
                            value={magicLinkData.name}
                            required
                          />
                          {magicLinkErrors.name ? (
                            <div className="text-red-500 text-sm">
                              {magicLinkErrors.name}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="form-item flex mt-4 flex-col gap-2 ">
                        <label
                          className="text-[10px] lg:text-base text-[#718096]"
                          htmlFor="email"
                        >
                          Email address
                        </label>
                        <div className="relative">
                          <input
                            className="bg-[#36383f] w-full text-[#fff] focus:outline-none text-[12px] lg:text-base rounded-[5px] p-3 lg:p-3 "
                            placeholder="Enter your e-mail"
                            type="email"
                            onChange={(e) => {
                              setMagicLinkData({
                                ...magicLinkData,
                                email: e.target.value,
                              });
                            }}
                            value={magicLinkData.email}
                            required
                          />
                          {magicLinkErrors.email ? (
                            <div className="text-red-500 text-sm">
                              {magicLinkErrors.email}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="form-item mt-4 flex flex-col gap-2 ">
                        <label
                          className="text-[10px] lg:text-base text-[#718096]"
                          htmlFor="phone_number"
                        >
                          Phone number
                        </label>
                        <input
                          type="hidden"
                          name="country_code"
                          value={magicLinkData.country_code}
                        />
                        <div className="flex w-full gap-2 items-start ">
                          <div className="w-[48px] h-[48px] ">
                            <PhoneInput
                              country={"us"}
                              value={magicLinkData.country_code}
                            onChange={(value, country) => {
                              setMagicLinkData({
                                ...magicLinkData,
                                country_code: `+${country.dialCode}`,
                              });
                            }}
                              inputStyle={{
                                display: "none",
                              }}
                              buttonStyle={{
                                borderRadius: "6px",
                                height: "40px",
                                border: "1px solid #ccc",
                              }}
                              dropdownStyle={{
                                zIndex: 9999,
                              }}
                              disableCountryCode={true}
                              disableDropdown={false}
                              enableAreaCodes={false}
                              className="min-w-[38px] detailsCustomInput "
                            />
                          </div>
                          <input
                            type="text"
                            name="phone_number"
                            placeholder="1234567890"
                            className="bg-[#36383F] w-full text-white focus:outline-none text-sm lg:text-base rounded-[5px] p-3"
                            value={magicLinkData.phone_number}
                            pattern="^[0-9]+$"
                            title="Only numbers are allowed"
                            onChange={(e) => {
                              const cleaned = e.target.value.replace(/[^0-9]/g, "");
                              setMagicLinkData({
                                ...magicLinkData,
                                phone_number: cleaned,
                              });
                            }}
                          />
                        </div>

                        {magicLinkErrors.phone_number && (
                          <div className="text-red-500 text-sm">{magicLinkErrors.phone_number}</div>
                        )}
                      </div>
                      <div className="mt-6 lg:mt-[50px] flex items-start lg:items-start gap-2">
                        <input
                          type="checkbox"
                          className="custom_checkbox min-w-[24px] mt-2 lg:mt-1"
                          id="agree"
                          checked={magicLinkData.is_sms_on}
                          onChange={() =>
                            setMagicLinkData({
                              ...magicLinkData,
                              is_sms_on: !magicLinkData.is_sms_on,
                            })
                          }
                        />
                        <p className="font-urbanist text-sm lg:text-base text-[#9E9E9E]">
                          <span>I </span>
                          <span className="text-white font-[700] mb-0">
                            consent to receive SMS
                          </span>
                          <span>
                            {" "}
                            reminders and important event updates about this ticket
                            purchase.
                          </span>
                        </p>
                      </div>
                      <div className="mt-3 lg:mt-[50px] flex items-start lg:items-center gap-2">
                        <input
                          type="checkbox"
                          className="custom_checkbox mt-2 lg:mt-0"
                          id="agree"
                          checked={magicLinkData.agree}
                          onChange={() =>
                            setMagicLinkData({
                              ...magicLinkData,
                              agree: !magicLinkData.agree,
                            })
                          }
                        />
                        <p className="font-urbanist text-xs lg:text-base text-[#9E9E9E]">
                          I agree to the{" "}
                          <a
                            href="https://help.qoncertapp.com/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-[700] text-white hover:text-[#fa2cb4]"
                          >
                            Privacy
                          </a>
                          ,{" "}
                          <a
                            href="https://help.qoncertapp.com/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-[700] text-white hover:text-[#fa2cb4]"
                          >
                            Terms
                          </a>{" "}
                          <span className="text-white ">and </span>
                          <a
                            href="https://help.qoncertapp.com/purchase-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-[700] text-white hover:text-[#fa2cb4]"
                          >
                            Purchase Terms
                          </a>
                        </p>
                      </div>

                      {magicLinkErrors.agree ? (
                        <div className="text-red-500 text-sm">
                          {magicLinkErrors.agree}
                        </div>
                      ) : null}
                    </div>
                  </motion.form>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                    className="mt-5 lg:mt-12 pt-1 relative"
                  >
                    <div className="absolute w-[120%] left-[-10%]">
                      <svg
                        width="100%"
                        height="2"
                        viewBox="0 0 694 2"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <line y1="1" x2="694.001" y2="1" stroke="#332D41" />
                      </svg>
                    </div>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                    onClick={() => {
                      router.push("/login");
                    }}
                    className={`mt-2 lg:mt-5 cursor-pointer text-center text-xs lg:text-base ${isUpdateProfile ? "hidden" : ""} `}
                  >
                    Already have an account?{" "}
                    <span className="gradient_login_txt">Login</span>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ duration: 0.5, delay: 0.8 }}
                    className="mt-5 lg:mt-5 modal-footer flex flex-col md:flex-row gap-7 items-center justify-end"
                  >
                    <button
                      onClick={() => {
                        setCurrentTab("password")
                      }}
                      className={`bg-transparent border-2 border-[#2B2D35] min-w-max font-[600] text-[14px] font-urbanist lg:text-base py-[10px] lg:py-[20px] lg:leading-[10px] px-[62px] w-[179px] lg:w-[340px] rounded-[10px] flex items-center justify-center gap-2 disabled:opacity-50 ${isUpdateProfile ? "hidden" : ""}`}
                    >
                      Choose Password
                    </button>
                    <button
                      onClick={() => {
                        setSubmittingLink(true)
                        sendMagicLink()
                      }}
                      disabled={submitingLink}
                      className={`main_btn_bg min-w-fit font-[600] text-[14px] font-urbanist lg:text-base py-[10px] lg:py-[20px] lg:leading-[10px] px-[62px] ${isUpdateProfile ? "w-full" : "w-[179px] lg:w-[340px]"} rounded-[10px] flex items-center justify-center gap-2 disabled:opacity-50`}
                    >
                      {submitingLink ? (
                        <>
                          <svg
                            className="animate-spin h-2 w-2 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                            ></path>
                          </svg>
                          <span>{isUpdateProfile ? 'Adding...' : 'Sending...'}</span>
                        </>
                      ) : (
                        isUpdateProfile ? 'Add Profile' : 'Send Magic Link'
                      )}
                    </button>
                  </motion.div>
                </div>
              </motion.div>
            ) : (
              <motion.div initial={{ opacity: 0 }} animate={!isLoading ? { opacity: 1 } : { opacity: 0 }} transition={{ duration: 0.5, delay: 0.4 }} className="mt-6 lg:mt-0 md:px-8">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="lg:mt-11 createeAcc text-center text-xl"
                >
                  <p>Create Account</p>
                </motion.div>
                <motion.form
                  initial={{ opacity: 0 }}
                  animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  action=""
                  className="mt-5 lg:pl-0 lg:mt-7"
                >
                  <div className="">
                    <div className="form-item mt-4 flex flex-col gap-2 ">
                      <label
                        className="text-[10px] lg:text-base text-[#718096]"
                        htmlFor="last_name"
                      >
                        Name
                      </label>
                      <div className="relative">
                        <input
                          className="bg-[#36383f] w-full text-[#fff] focus:outline-none text-[12px] lg:text-base rounded-[5px] p-3 lg:p-3 "
                          placeholder="Enter your Name"
                          type="text"
                          name="name"
                          {...formik.getFieldProps("name")}
                          required
                        />
                        {formik.touched.name && formik.errors.name ? (
                          <div className="text-red-500 text-sm">
                            {formik.errors.name}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="form-item flex flex-col gap-2 ">
                      <label
                        className="text-[10px] lg:text-base text-[#718096]"
                        htmlFor="email"
                      >
                        Email address
                      </label>
                      <div className="relative">
                        <input
                          className="bg-[#36383f] w-full text-[#fff] focus:outline-none text-[12px] lg:text-base rounded-[5px] p-3 lg:p-3 "
                          placeholder="Enter your e-mail"
                          type="email"
                          {...formik.getFieldProps("email")}
                          id="email"
                          required
                        />
                        {formik.touched.email && formik.errors.email ? (
                          <div className="text-red-500 text-sm">
                            {formik.errors.email}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="form-item mt-4 flex flex-col gap-2 ">
                      <label
                        className="text-[10px] lg:text-base text-[#718096]"
                        htmlFor="phone_number"
                      >
                        Phone number
                      </label>
                      <input
                        type="hidden"
                        name="country_code"
                        value={formik.country_code}
                      />
                      <div className="flex w-full gap-2 items-start ">
                        <div className="w-[48px] h-[48px] ">
                          <PhoneInput
                            country={"us"}
                            value={formik.values.country_code}
                            onChange={(value, country) => {
                              formik.setFieldValue("country_code", `+${country.dialCode}`);
                            }}
                            inputStyle={{
                              display: "none",
                            }}
                            buttonStyle={{
                              borderRadius: "6px",
                              height: "40px",
                              border: "1px solid #ccc",
                            }}
                            dropdownStyle={{
                              zIndex: 9999,
                            }}
                            disableCountryCode={true}
                            disableDropdown={false}
                            enableAreaCodes={false}
                            className="min-w-[38px] detailsCustomInput "
                          />
                        </div>
                        <input
                          type="text"
                          name="phone_number"
                          placeholder="1234567890"
                          className="bg-[#36383F] w-full text-white focus:outline-none text-sm lg:text-base rounded-[5px] p-3"
                          value={formik.values.phone_number}
                          pattern="^[0-9]+$"
                          title="Only numbers are allowed"
                          onChange={(e) => {
                            const cleaned = e.target.value.replace(/[^0-9]/g, "");
                            formik.setFieldValue("phone_number", cleaned);
                          }}
                        />
                      </div>

                      {formik.touched.phone_number && formik.errors.phone_number && (
                        <div className="text-red-500 text-sm">{formik.errors.phone_number}</div>
                      )}
                    </div>
                    <div className="form-item mt-4 flex flex-col gap-2 ">
                      <label
                        className="text-[10px] lg:text-base text-[#718096]"
                        htmlFor="password"
                      >
                        Password
                      </label>
                      <div className="relative">
                        <input
                          className="bg-[#36383f] w-full text-[#fff] focus:outline-none text-xs lg:text-base  rounded-[5px] p-3 lg:p-3 "
                          placeholder="Enter your password"
                          type="password"
                          id="password"
                          {...formik.getFieldProps("password")}
                          required
                        />
                        {formik.touched.password && formik.errors.password ? (
                          <div className="text-red-500 text-sm">
                            {formik.errors.password}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="mt-6 lg:mt-[50px] flex items-start lg:items-start gap-2">
                      <input
                        type="checkbox"
                        className="custom_checkbox min-w-[24px] mt-2 lg:mt-1"
                        id="agree"
                        checked={formik.values.is_sms_on}
                        onChange={() =>
                          formik.setFieldValue(
                            "is_sms_on",
                            !formik.values.is_sms_on
                          )
                        }
                      />
                      <p className="font-urbanist text-sm lg:text-base text-[#9E9E9E]">
                        <span>I </span>
                        <span className="text-white font-[700] mb-0">
                          consent to receive SMS
                        </span>
                        <span>
                          {" "}
                          reminders and important event updates about this ticket
                          purchase.
                        </span>
                      </p>
                    </div>
                    <div className="mt-3 lg:mt-[50px] flex items-start lg:items-center gap-2">
                      <input
                        type="checkbox"
                        className="custom_checkbox mt-2 lg:mt-0"
                        id="agree"
                        checked={formik.values.agree}
                        onChange={() =>
                          formik.setFieldValue("agree", !formik.values.agree)
                        }
                      />
                      <p className="font-urbanist text-xs lg:text-base text-[#9E9E9E]">
                        I agree to the{" "}
                        <a
                          href="https://help.qoncertapp.com/privacy-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-[700] text-white hover:text-[#fa2cb4]"
                        >
                          Privacy
                        </a>
                        ,{" "}
                        <a
                          href="https://help.qoncertapp.com/terms"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-[700] text-white hover:text-[#fa2cb4]"
                        >
                          Terms
                        </a>{" "}
                        <span className="text-white ">and </span>
                        <a
                          href="https://help.qoncertapp.com/purchase-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-[700] text-white hover:text-[#fa2cb4]"
                        >
                          Purchase Terms
                        </a>
                      </p>
                    </div>

                    {formik.touched.agree && formik.errors.agree ? (
                      <div className="text-red-500 text-sm">
                        {formik.errors.agree}
                      </div>
                    ) : null}
                  </div>
                </motion.form>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  className="mt-5 lg:mt-12 pt-1 relative"
                >
                  <div className="absolute w-[120%] left-[-10%]">
                    <svg
                      width="100%"
                      height="2"
                      viewBox="0 0 694 2"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <line y1="1" x2="694.001" y2="1" stroke="#332D41" />
                    </svg>
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                  onClick={() => {
                    router.push("/login");
                  }}
                  className="mt-2 lg:mt-5 cursor-pointer text-center text-xs lg:text-base"
                >
                  Already have an account?{" "}
                  <span className="gradient_login_txt">Login</span>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                  className="mt-5 lg:mt-5 modal-footer flex flex-col md:flex-row gap-7 items-center justify-end"
                >
                  <button
                    onClick={() => {
                      setCurrentTab("magicLink")
                    }}
                    className="bg-transparent border-2 border-[#2B2D35] min-w-max font-[600] text-[14px] font-urbanist lg:text-base py-[10px] lg:py-[20px] lg:leading-[10px] px-[62px] w-[179px] lg:w-[340px] rounded-[10px] flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    Choose Magic Link
                  </button>
                  <button
                    id="submit-button-id"
                    onClick={() => {
                      if (!isSubmitting) {
                        formik.handleSubmit();
                      }
                    }}
                    disabled={isSubmitting}
                    className="main_btn_bg font-[800] text-[14px] font-urbanist lg:text-base py-[10px] lg:py-[20px] lg:leading-[10px] px-[62px] w-[179px] lg:w-[340px] rounded-[10px] flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="animate-spin h-2 w-2 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                          ></path>
                        </svg>
                        <span>Submitting...</span>
                      </>
                    ) : (
                      "Continue"
                    )}
                  </button>
                </motion.div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </User>
  );
}